import { Metadata } from "next";
import { ISBLink } from "../types/sb";
import { logServiceEvent, logError } from "../helper/logger"; // Importing logger functions

const SERVICE_NAME = "transformMetaData"; // Constant for service name

type MetaTag = {
  name?: string;
  property?: string;
  content: string;
};
type MetadataTransformer = (meta: Metadata, key: string, value: string) => void;
const safeSplit = (val: string | undefined, delimiter: string): string[] => {
  if (typeof val !== "string") return [];
  return val
    .split(delimiter)
    .map((v) => v.trim())
    .filter(Boolean);
};

const safeSplitField = (key: string | undefined, index: number): string => {
  if (!key || !key.includes(":")) return "";
  const parts = key.split(":");
  return parts.length > index ? parts[index] : "";
};

const safeString = (val: any, fallback: string) =>
  typeof val === "string" && val.trim?.() ? val : fallback;
const safeUrl = (val: any, fallback: string) =>
  typeof val === "string" && /^https?:\/\//.test(val) ? val : fallback;
const safeSlice = (key: string, prefixLength: number) =>
  key?.substring(prefixLength)?.trim?.();

const metadataMap: Record<string, MetadataTransformer> = {
  title: (meta, _, val) => {
    meta.title = val;
  },
  description: (meta, _, val) => {
    meta.description = val;
  },
  keywords: (meta, _, val) => {
    meta.keywords = safeSplit(val, ",");
  },
  "application-name": (meta, _, val) => {
    meta.applicationName = val;
  },
  author: (meta, _, val) => {
    meta.authors = [{ name: val }];
  },
  creator: (meta, _, val) => {
    meta.creator = val;
  },
  publisher: (meta, _, val) => {
    meta.publisher = val;
  },
  robots: (meta, _, val) => {
    meta.robots = val;
  },
  abstract: (meta, _, val) => {
    meta.abstract = val;
  },
  category: (meta, _, val) => {
    meta.category = val;
  },
  classification: (meta, _, val) => {
    meta.classification = val;
  },
  generator: (meta, _, val) => {
    meta.generator = val;
  },
  archives: (meta, _, val) => {
    meta.archives = safeSplit(val, ",");
  },
  assets: (meta, _, val) => {
    meta.assets = safeSplit(val, ",");
  },
  bookmarks: (meta, _, val) => {
    meta.bookmarks = safeSplit(val, ",");
  },
  prev: (meta, _, val) => {
    meta.pagination ??= { next: null, previous: null };
    meta.pagination.previous = val;
  },
  next: (meta, _, val) => {
    meta.pagination ??= { next: null, previous: null };
    meta.pagination.next = val;
  },
  // OpenGraph
  "og:*": (meta, key, val) => {
    const field = safeSlice(key, 3);
    if (!field) return;
    meta.openGraph ??= {};
    switch (field) {
      case "type": {
        const allowed = [
          "website",
          "article",
          "book",
          "profile",
          "video.movie",
        ];
        const normalized = String(val).toLowerCase();
        meta.openGraph["type"] = allowed.includes(normalized)
          ? normalized
          : "website";
        break;
      }
      case "url":
        meta.openGraph.url = safeUrl(val, "");
        break;
      case "title":
        meta.openGraph.title = safeString(val, "");
        break;
      case "description":
        meta.openGraph.description = safeString(val, "");
        break;
      case "image":
        meta.openGraph.images = [{ url: safeUrl(val, "") }];
        break;
      case "site_name":
        meta.openGraph.siteName = safeString(val, "");
        break;
      default:
        (meta.other as any)[key] = val;
    }
  },
  // Twitter
  "twitter:*": (meta, key, val) => {
    const field = safeSlice(key, 8);
    if (!field) return;
    meta.twitter ??= {};
    switch (field) {
      case "card":
        meta.twitter["card"] =
          val === "summary_large_image" ? "summary_large_image" : "summary";
        break;
      case "title":
        meta.twitter.title = safeString(val, "");
        break;
      case "description":
        meta.twitter.description = safeString(val, "");
        break;
      case "site":
        meta.twitter.site = safeString(val, "");
        break;
      case "siteId": 
        meta.twitter.siteId = safeString(val, "");
        break;
      case "creator":
        meta.twitter.creator = safeString(val, "");
        break;
      case "creatorId":
        meta.twitter.creatorId = safeString(val, "");
        break;
      case "image":
        meta.twitter.images = [{ url: safeUrl(val, "") }];
        break;
      default:
        (meta.other as any)[key] = val;
    }
  },
  // Facebook
  "fb:*": (meta, key, val) => {
    const field = safeSplitField(key, 1);
    if (!field) return;
    meta.facebook ??= {
      admins: null,
    };
    meta.facebook[field] = val;
  },
  // Pinterest
  "pinterest:*": (meta, key, val) => {
    const field = safeSplitField(key, 1);
    if (!field) return;
    meta.pinterest ??= {
      richPin: null,
    };
    meta.pinterest[field] = val;
  },
  // Apple Web App
  "apple:*": (meta, key, val) => {
    const field = safeSlice(key, 6);
    if (!field) return;
    meta.appleWebApp ??= {};
    switch (field) {
      case "title":
        meta.appleWebApp["title"] = safeString(val, "");
        break;
      case "statusBarStyle":
        meta.appleWebApp["statusBarStyle"] = [
          "black",
          "default",
          "black-translucent",
        ].includes(val)
          ? val
          : "default";
        break;
      default:
        (meta.appleWebApp as any)[field] = val;
    }
  },
  // iTunes
  "itunes:*": (meta, key, val) => {
    const field = safeSlice(key, 7);
    if (!field) return;
    if (field === "appId") {
      meta.itunes = {
        appId: safeString(val, ""),
      };
    }
  },

  // App Links
  "al:*": (meta, key, val) => {
    const field = safeSlice(key, 9);
    if (!field) return;
    meta.appLinks ??= {};
    if (field === "ios" && Array.isArray(val)) {
      meta.appLinks.ios = val;
    }
    if (field === "android" && Array.isArray(val)) {
      meta.appLinks.android = val;
    }
  },
  // Verification
  "*verification*": (meta, key, val) => {
    const platform = key?.split("-")?.[0];
    if (!platform) return;
    meta.verification ??= {};
    meta.verification[platform] = val;
  },
};
function cleanNulls(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(cleanNulls).filter((item) => item != null);
  } else if (typeof obj === "object" && obj !== null) {
    const cleaned: any = {};
    for (const key in obj) {
      const value = cleanNulls(obj[key]);
      if (
        value != null &&
        (typeof value !== "object" || Object.keys(value).length > 0)
      ) {
        cleaned[key] = value;
      }
    }
    return Object.keys(cleaned).length ? cleaned : null;
  }
  return obj ?? null;
}

function buildAlternatesFromLinkTags(linkArray) {
  const alternates = {
    canonical: undefined,
    languages: {},
  };
  for (const link of linkArray) {
    if (link.rel === "canonical") {
      if (!link.hrefLang) {
        alternates.canonical = link.href;
      } else {
        alternates.languages[link.hrefLang] = link.href;
      }
    }
  }
  return alternates;
}
export function transformMetaArrayToMetadata(
  metaArray: MetaTag[],
  title: string,
  canonicalLink: ISBLink[],
  cmsPageName?: string
): Metadata {
  try {
    if (!Array.isArray(metaArray)) {
      logServiceEvent(SERVICE_NAME, "metaArray is not an array — skipping meta transformation", {
        metaArrayType: typeof metaArray,
        titleProvided: !!title,
        cmsPageName
      });
    }
    const effectiveMeta: MetaTag[] = Array.isArray(metaArray) ? metaArray : [];

    logServiceEvent(SERVICE_NAME, "Starting metadata transformation", {
      metaArrayLength: effectiveMeta.length,
      titleProvided: !!title,
      canonicalLinkLength: canonicalLink?.length,
      cmsPageName
    });
    const alternates = buildAlternatesFromLinkTags(canonicalLink);

    const metadata: Metadata = {
      metadataBase: null,
      title: title,
      description: null,
      applicationName: null,
      authors: null,
      generator: null,
      keywords: null,
      referrer: null,
      themeColor: null,
      colorScheme: null,
      viewport: null,
      creator: null,
      publisher: null,
      robots: null,
      alternates: alternates,
      icons: null,
      openGraph: null,
      manifest: null,
      twitter: null,
      facebook: null,
      pinterest: null,
      verification: null,
      appleWebApp: null,
      formatDetection: null,
      itunes: null,
      abstract: null,
      appLinks: null,
      archives: null,
      assets: null,
      bookmarks: null,
      pagination: {
        previous: null,
        next: null,
      },
      category: null,
      classification: null,
      other: {},
    };
    for (const tag of effectiveMeta) {
      try {
        const key = (tag.name || tag.property)?.toLowerCase();
        const val = tag.content;
        if (!key || typeof val !== "string" || val.trim() === "") continue;
        let matched = false;
        for (const pattern in metadataMap) {
          if (
            pattern === key ||
            (pattern.endsWith("*") && key.startsWith(pattern.slice(0, -1))) ||
            (pattern.startsWith("*") && key.includes(pattern.slice(1)))
          ) {
            try {
              metadataMap[pattern](metadata, key, val);
            } catch {
              // Silently skip transformation error
            }
            matched = true;
            break;
          }
        }
        if (!matched) {
          metadata.other![tag.name || tag.property!] = val;
        }
      } catch (error) {
        logError(SERVICE_NAME, error, {
          actualTag: tag,
          metaArrayLength: effectiveMeta.length,
          titleProvided: !!title,
          canonicalLinkLength: canonicalLink?.length,
        });
        continue;
      }
    }
    return cleanNulls(metadata) as Metadata;
  } catch (error) {
    logError(SERVICE_NAME, error, {
      metaArrayLength: Array.isArray(metaArray) ? metaArray.length : undefined,
      titleProvided: !!title,
      canonicalLinkLength: canonicalLink?.length,
    });
    return null;
  }
}
